// Flat, taggable vocabulary for the Vocabulary tab. One row = one record with
// { id, english, portuguese, category, level, notes }.
//
// Filter by `level` and by `category` independently — the two combine. The UI
// builds its level filter from VOCAB_LEVELS (derived from the data), never a
// hardcoded "A1 or A2", so adding B1 later is a data-only change. Categories
// are a fixed set of 10 umbrellas (below) meant to hold steady across levels;
// a genuinely new category is a deliberate schema change, not an import.
//
// Adding a CEFR level (B1, B2 …) = adding rows with that `level`. Never
// duplicate the table or branch per level. A later row may supersede an
// earlier one (re-categorised, re-levelled) — just overwrite it here; the
// schema keeps no history.
//
// `portuguese` may hold several valid forms separated by " / " (e.g.
// "Olá / Oi"). Register/formality nuance lives in `notes`, not as extra
// rows. `article` carries a noun's definite article (o / a / os / as),
// shown as a muted prefix so the gender is visible; one string applies to
// every form, an array lines up with the " / " forms position-for-position
// (e.g. "o / a" for filho / filha). Omitted for non-nouns.

// Order matters: this is a soft learning sequence (greetings → question
// words → numbers → …), and the Vocabulary tab's category filter renders the
// options in this order so it reads left-to-right as "start here".
export const VOCAB_CATEGORIES = [
  'greetings_courtesy',
  'question_words',
  'numbers',
  'time_sequence',
  'descriptive',
  'people_relationships',
  'getting_around',
  'transactions',
  'daily_life',
  'communication_opinions',
] as const

export type VocabCategory = (typeof VOCAB_CATEGORIES)[number]

// Display labels for the fixed category set. A new category must be added here
// AND to VOCAB_CATEGORIES on purpose — an import must not invent one.
export const VOCAB_CATEGORY_LABEL: Record<VocabCategory, string> = {
  greetings_courtesy: 'Greetings & Courtesy',
  question_words: 'Questions',
  numbers: 'Numbers',
  time_sequence: 'Time & Sequence',
  descriptive: 'Descriptive',
  people_relationships: 'People & Relationships',
  getting_around: 'Getting Around',
  transactions: 'Transactions',
  daily_life: 'Daily Life',
  communication_opinions: 'Communication & Opinions',
}

export type VocabRow = {
  id: string
  english: string
  portuguese: string // one or more forms, separated by " / "
  category: VocabCategory
  level: string // CEFR: 'A1', 'A2', 'B1' … — open-ended, read from the data
  notes: string | null
  article?: string | string[] // definite article for a noun form: o / a / os / as
}

// [english, portuguese, category, level, notes, article]. A blank note or
// article becomes null / undefined; article "o / a" (slash) means one per form.
const RAW: Array<[string, string, VocabCategory, string, string, string]> = [
  // ── Greetings & Courtesy ────────────────────────────────────────────────
  [`Hello / Hi`, `Olá / Oi`, `greetings_courtesy`, `A1`, `oi is casual & most common; Olá slightly more neutral/formal`, ``],
  [`Good morning`, `Bom dia`, `greetings_courtesy`, `A1`, ``, ``],
  [`Good afternoon`, `Boa tarde`, `greetings_courtesy`, `A1`, ``, ``],
  [`Good evening/night`, `Boa noite`, `greetings_courtesy`, `A1`, `used for both "good evening" and "goodnight"`, ``],
  [`How are you?`, `Como vai? / Tudo bem?`, `greetings_courtesy`, `A1`, `tudo bem? is far more common in casual speech`, ``],
  [`I'm fine, thanks`, `Tudo bem, obrigado(a)`, `greetings_courtesy`, `A1`, `-o if male, -a if female`, ``],
  [`Please`, `Por favor`, `greetings_courtesy`, `A1`, ``, ``],
  [`Thank you`, `Obrigado(a)`, `greetings_courtesy`, `A1`, `same gender rule as above`, ``],
  [`You're welcome`, `De nada`, `greetings_courtesy`, `A1`, ``, ``],
  [`Excuse me`, `Com licença`, `greetings_courtesy`, `A1`, `say BEFORE intruding — passing by, interrupting, sitting down`, ``],
  [`Sorry`, `Desculpa / Desculpe`, `greetings_courtesy`, `A1`, `reactive apology for a minor slip; Desculpa = informal (friends/peers), Desculpe = formal/polite (strangers, elders, professional settings)`, ``],
  [`I'm sorry (sincere)`, `Sinto muito`, `greetings_courtesy`, `A1`, `genuine remorse, condolences — deeper than "desculpa"`, ``],
  [`Yes / No`, `Sim / Não`, `greetings_courtesy`, `A1`, ``, ``],
  [`Goodbye`, `Tchau`, `greetings_courtesy`, `A1`, `casual, everyday goodbye`, ``],
  [`See you soon`, `Até logo`, `greetings_courtesy`, `A1`, ``, ``],
  [`See you later`, `Até mais`, `greetings_courtesy`, `A1`, ``, ``],
  [`Nice to meet you`, `Prazer`, `greetings_courtesy`, `A1`, `shorthand for "muito prazer", appropriate in both casual and polite settings`, ``],
  [`Nice to meet you (fuller)`, `Muito prazer`, `greetings_courtesy`, `A1`, `fuller version of "prazer", for more formal intros`, ``],
  // ── Question Words ─────────────────────────────────────────────────────
  [`what`, `o que / qual`, `question_words`, `A1`, `o que = define/explain; qual = select from a set (e.g. "Qual é sua cor favorita?"). Name/address always use qual. It only takes an accent — o quê — at the end of a question.`, ``],
  [`who`, `quem`, `question_words`, `A1`, ``, ``],
  [`where`, `onde`, `question_words`, `A1`, ``, ``],
  [`when`, `quando`, `question_words`, `A1`, ``, ``],
  [`why`, `por que`, `question_words`, `A1`, `por que in a question; por quê at the end of one; porque (one word) = because`, ``],
  [`how`, `como`, `question_words`, `A1`, ``, ``],
  [`how much/many`, `quanto(a) / quantos(as)`, `question_words`, `A1`, `agrees with gender/number of noun`, ``],
  [`which`, `qual`, `question_words`, `A1`, ``, ``],
  [`Is there...?`, `Tem...? / Há...?`, `question_words`, `A1`, `tem = everyday spoken; Há = formal/written`, ``],
  [`Can you...?`, `Você pode...?`, `question_words`, `A1`, ``, ``],
  // ── Getting to know someone ──────────────────────────────────────────
  [`Where are you from?`, `De onde você é?`, `question_words`, `A1`, `classic icebreaker to ask someone's country or city of origin`, ``],
  [`How old are you?`, `Quantos anos você tem?`, `question_words`, `A1`, `uses the verb ter (to have), matching the answer "Tenho ... anos"`, ``],
  [`What do you do for a living? / What is your job?`, `Com o que você trabalha? / Qual é o seu trabalho?`, `question_words`, `A2`, `two common ways to ask about someone's profession`, ``],
  [`How long have you been studying Portuguese?`, `Há quanto tempo você estuda português?`, `question_words`, `B1`, `uses the verb haver (há) + a time span — the question form of "estudo há X anos"`, ``],
  [`Is there any other language you want to learn in the future?`, `Existe algum outro idioma que você quer aprender no futuro?`, `question_words`, `B1`, `a natural follow-up when swapping language-learning goals`, ``],
  [`how`, `quão`, `question_words`, `B1`, `before an adjective or adverb; asks or stresses the degree of a quality — "quão difícil" = how hard. Formal and written; in speech Brazilians say "que" or "como" instead`, ``],
  // ── Numbers ───────────────────────────────────────────────────────────
  [`0–10`, `zero, um, dois, três, quatro, cinco, seis, sete, oito, nove, dez`, `numbers`, `A1`, ``, ``],
  [`11–100 (key markers)`, `onze, doze, treze, quatorze, quinze, dezesseis, dezessete, dezoito, dezenove, vinte, trinta, quarenta, cinquenta, sessenta, setenta, oitenta, noventa, cem`, `numbers`, `A1`, `pattern after 20: vinte e um (21), trinta e dois (32), etc.`, ``],
  [`first / last`, `primeiro / último`, `numbers`, `A1`, ``, ``],
  [`100–1000`, `cem, duzentos, trezentos, quinhentos, mil`, `numbers`, `A2`, `duzentos/trezentos agree with gender (duzentas pessoas)`, ``],
  [`ordinals (2nd–10th)`, `segundo, terceiro, quarto, quinto...décimo`, `numbers`, `A2`, `builds on primeiro/último`, ``],
  // ── Time & Sequence ───────────────────────────────────────────────────
  [`Monday–Sunday`, `segunda-feira, terça-feira, quarta-feira, quinta-feira, sexta-feira, sábado, domingo`, `time_sequence`, `A1`, `in speech, "-feira" is often dropped (segunda, terça...)`, ``],
  [`today`, `hoje`, `time_sequence`, `A1`, ``, ``],
  [`tomorrow`, `amanhã`, `time_sequence`, `A1`, ``, ``],
  [`yesterday`, `ontem`, `time_sequence`, `A1`, ``, ``],
  [`now`, `agora`, `time_sequence`, `A1`, ``, ``],
  [`later`, `mais tarde`, `time_sequence`, `A1`, ``, ``],
  [`morning`, `manhã`, `time_sequence`, `A1`, ``, `a`],
  [`afternoon`, `tarde`, `time_sequence`, `A1`, ``, `a`],
  [`night`, `noite`, `time_sequence`, `A1`, ``, `a`],
  [`week`, `semana`, `time_sequence`, `A1`, ``, `a`],
  [`month`, `mês`, `time_sequence`, `A1`, ``, `o`],
  [`year`, `ano`, `time_sequence`, `A1`, ``, `o`],
  [`What time is it?`, `Que horas são?`, `time_sequence`, `A1`, ``, ``],
  [`o'clock`, `hora(s)`, `time_sequence`, `A1`, ``, `a`],
  [`months (january–december)`, `janeiro / fevereiro / março / abril / maio / junho / julho / agosto / setembro / outubro / novembro / dezembro`, `time_sequence`, `A2`, `not capitalized in Portuguese`, ``],
  [`seasons`, `verão, outono, inverno, primavera`, `time_sequence`, `A2`, ``, ``],
  [`next week / last week`, `semana que vem / semana passada`, `time_sequence`, `A2`, ``, ``],
  [`half past`, `e meia`, `time_sequence`, `A2`, `"são duas e meia" = it's 2:30`, ``],
  [`quarter to`, `para as...`, `time_sequence`, `A2`, `"quinze para as três" = 2:45`, ``],
  [`already / not yet`, `já / ainda não`, `time_sequence`, `A2`, `high-frequency, used constantly`, ``],
  [`ago`, `atrás / há`, `time_sequence`, `A2`, `"dois dias atrás" or "há dois dias" — both work`, ``],
  [`soon`, `em breve`, `time_sequence`, `A2`, ``, ``],
  [`next month`, `mês que vem`, `time_sequence`, `A2`, ``, ``],
  [`last month`, `mês passado`, `time_sequence`, `A2`, ``, ``],
  [`then / after that`, `depois / aí`, `time_sequence`, `A2`, `"aí" very common in spoken narration`, ``],
  // ── Descriptive ──────────────────────────────────────────────────────
  [`red`, `vermelho`, `descriptive`, `A1`, ``, ``],
  [`blue`, `azul`, `descriptive`, `A1`, ``, ``],
  [`green`, `verde`, `descriptive`, `A1`, ``, ``],
  [`yellow`, `amarelo`, `descriptive`, `A1`, ``, ``],
  [`black`, `preto`, `descriptive`, `A1`, ``, ``],
  [`white`, `branco`, `descriptive`, `A1`, ``, ``],
  [`orange`, `laranja`, `descriptive`, `A1`, `same word as the fruit`, `o`],
  [`pink`, `rosa`, `descriptive`, `A1`, ``, `o`],
  [`gray`, `cinza`, `descriptive`, `A1`, ``, ``],
  [`purple`, `roxo`, `descriptive`, `A1`, ``, ``],
  [`shirt`, `camisa`, `descriptive`, `A1`, ``, `a`],
  [`t-shirt`, `camiseta`, `descriptive`, `A1`, ``, `a`],
  [`pants`, `calça`, `descriptive`, `A1`, `singular in Portuguese, unlike English "pants"`, `a`],
  [`shorts`, `short / bermuda`, `descriptive`, `A1`, `"short" is an everyday Brazilian loanword (um short, os shorts); bermuda is the longer, knee-length kind`, `o/a`],
  [`dress`, `vestido`, `descriptive`, `A1`, ``, `o`],
  [`skirt`, `saia`, `descriptive`, `A1`, ``, `a`],
  [`shoes`, `sapatos`, `descriptive`, `A1`, ``, `os`],
  [`jacket`, `jaqueta`, `descriptive`, `A1`, ``, `a`],
  [`sweater`, `suéter`, `descriptive`, `A1`, ``, `o`],
  [`socks`, `meias`, `descriptive`, `A1`, ``, `as`],
  [`hat`, `chapéu / boné`, `descriptive`, `A1`, `chapéu = brimmed hat; boné = baseball cap`, `o`],
  [`coat`, `casaco`, `descriptive`, `A1`, ``, `o`],
  [`good / bad`, `bom / ruim`, `descriptive`, `A1`, ``, ``],
  [`big / small`, `grande / pequeno`, `descriptive`, `A1`, ``, ``],
  [`hot / cold`, `quente / frio`, `descriptive`, `A1`, ``, ``],
  [`new / old`, `novo / velho`, `descriptive`, `A1`, `velho is for objects/informal; for people "idoso" is more polite`, ``],
  [`easy / difficult`, `fácil / difícil`, `descriptive`, `A1`, ``, ``],
  [`fast / slow`, `rápido / lento`, `descriptive`, `A1`, ``, ``],
  [`expensive / cheap`, `caro / barato`, `descriptive`, `A1`, ``, ``],
  [`tired / rested`, `cansado / descansado`, `descriptive`, `A2`, `cansado covers both physical and emotional/mental tiredness; use with estar`, ``],
  [`comfortable / uncomfortable`, `confortável / desconfortável`, `descriptive`, `A2`, ``, ``],
  [`loud / quiet`, `barulhento / quieto`, `descriptive`, `A2`, ``, ``],
  [`clean / dirty`, `limpo / sujo`, `descriptive`, `A2`, ``, ``],
  [`strong / weak`, `forte / fraco`, `descriptive`, `A2`, ``, ``],
  [`more than / less than`, `mais que / menos que`, `descriptive`, `A2`, ``, ``],
  [`as...as`, `tão...quanto`, `descriptive`, `A2`, `"tão grande quanto" = as big as`, ``],
  [`better / worse`, `melhor / pior`, `descriptive`, `A2`, ``, ``],
  [`the most / the least`, `o mais / o menos`, `descriptive`, `A2`, ``, ``],
  // ── B1: nuanced & emotional adjectives ───────────────────────────────
  [`boring / interesting`, `chato / interessante`, `descriptive`, `B1`, ``, ``],
  [`surprising`, `surpreendente`, `descriptive`, `B1`, ``, ``],
  [`disappointed`, `decepcionado(a)`, `descriptive`, `B1`, ``, ``],
  [`proud`, `orgulhoso(a)`, `descriptive`, `B1`, ``, ``],
  [`confused`, `confuso(a)`, `descriptive`, `B1`, ``, ``],
  [`worth it`, `vale a pena`, `descriptive`, `B1`, `very common standalone phrase ("vale a pena!")`, ``],
  // ── People & Relationships ──────────────────────────────────────────
  [`mother / father`, `mãe / pai`, `people_relationships`, `A1`, ``, `a/o`],
  [`son / daughter`, `filho / filha`, `people_relationships`, `A1`, ``, `o/a`],
  [`brother / sister`, `irmão / irmã`, `people_relationships`, `A1`, ``, `o/a`],
  [`grandmother / grandfather`, `avó / avô`, `people_relationships`, `A1`, `accent flips the sound: avó (ó open) vs avô (ô closed)`, `a/o`],
  [`husband / wife`, `marido / esposa`, `people_relationships`, `A1`, ``, `o/a`],
  [`friend`, `amigo(a)`, `people_relationships`, `A1`, ``, `o`],
  [`family`, `família`, `people_relationships`, `A1`, ``, `a`],
  [`kids/children`, `filhos / crianças`, `people_relationships`, `A1`, `filhos = "children" (as offspring); crianças = "kids" generally`, `os/as`],
  [`cousin`, `primo / prima`, `people_relationships`, `A2`, ``, `o/a`],
  [`uncle / aunt`, `tio / tia`, `people_relationships`, `A2`, ``, `o/a`],
  [`nephew / niece`, `sobrinho / sobrinha`, `people_relationships`, `A2`, ``, `o/a`],
  [`father-in-law / mother-in-law`, `sogro / sogra`, `people_relationships`, `A2`, ``, `o/a`],
  [`happy / sad`, `feliz / triste`, `people_relationships`, `A1`, `moved from Adjectives — these are emotional states, use with "estar"`, ``],
  [`bored`, `entediado(a)`, `people_relationships`, `A2`, ``, ``],
  [`worried`, `preocupado(a)`, `people_relationships`, `A2`, ``, ``],
  [`excited`, `animado(a)`, `people_relationships`, `A2`, ``, ``],
  [`nervous`, `nervoso(a)`, `people_relationships`, `A2`, ``, ``],
  // ── B1: relationship status ──────────────────────────────────────────
  [`boyfriend / girlfriend`, `namorado / namorada`, `people_relationships`, `B1`, ``, ``],
  [`fiancé / fiancée`, `noivo / noiva`, `people_relationships`, `B1`, ``, ``],
  [`married / single / divorced`, `casado(a) / solteiro(a) / divorciado(a)`, `people_relationships`, `B1`, ``, ``],
  [`to get married`, `casar(-se)`, `people_relationships`, `B1`, ``, ``],
  [`to break up`, `terminar`, `people_relationships`, `B1`, `also means "to finish" in general`, ``],
  [`coworker`, `colega de trabalho`, `people_relationships`, `B1`, ``, ``],
  [`neighbor`, `vizinho(a)`, `people_relationships`, `B1`, ``, ``],
  // ── Getting Around ─────────────────────────────────────────────────
  [`above / on top of`, `em cima de`, `getting_around`, `A1`, ``, ``],
  [`under / below`, `embaixo de`, `getting_around`, `A1`, ``, ``],
  [`left`, `esquerda`, `getting_around`, `A1`, ``, `a`],
  [`right`, `direita`, `getting_around`, `A1`, ``, `a`],
  [`in front of`, `na frente de / em frente a`, `getting_around`, `A1`, `na frente de = everyday spoken default; Em frente a/de = more standard/written form. Interchangeable in casual speech, but em frente is the "correct" register if being precise`, ``],
  [`behind`, `atrás de`, `getting_around`, `A1`, ``, ``],
  [`next to / beside`, `ao lado de`, `getting_around`, `A1`, ``, ``],
  [`between`, `entre`, `getting_around`, `A1`, ``, ``],
  [`inside`, `dentro de`, `getting_around`, `A1`, ``, ``],
  [`outside`, `fora de`, `getting_around`, `A1`, ``, ``],
  [`near`, `perto de`, `getting_around`, `A1`, ``, ``],
  [`far`, `longe de`, `getting_around`, `A1`, ``, ``],
  [`straight ahead`, `em frente / reto`, `getting_around`, `A1`, `interchangeable synonyms when giving directions — no difference in when to use one vs. the other, e.g. "Siga em frente" / "Siga reto" both mean "go straight"`, ``],
  [`here / there`, `aqui / ali (or Lá)`, `getting_around`, `A1`, `ali = "over there" (visible/nearby); Lá = "there" (farther/general)`, ``],
  [`house / home`, `casa`, `getting_around`, `A1`, ``, `a`],
  [`work`, `trabalho`, `getting_around`, `A1`, ``, `o`],
  [`school`, `escola`, `getting_around`, `A1`, ``, `a`],
  [`store / shop`, `loja`, `getting_around`, `A1`, ``, `a`],
  [`market`, `mercado`, `getting_around`, `A1`, ``, `o`],
  [`bathroom`, `banheiro`, `getting_around`, `A1`, ``, `o`],
  [`street`, `rua`, `getting_around`, `A1`, ``, `a`],
  [`city`, `cidade`, `getting_around`, `A1`, ``, `a`],
  [`airport`, `aeroporto`, `getting_around`, `A1`, ``, `o`],
  [`hotel`, `hotel`, `getting_around`, `A1`, ``, `o`],
  [`pharmacy`, `farmácia`, `getting_around`, `A2`, ``, `a`],
  [`bank`, `banco`, `getting_around`, `A2`, ``, `o`],
  [`post office`, `correios`, `getting_around`, `A2`, `always plural in Portuguese`, `os`],
  [`gym`, `academia`, `getting_around`, `A2`, ``, `a`],
  [`park`, `parque`, `getting_around`, `A2`, ``, `o`],
  [`church`, `igreja`, `getting_around`, `A2`, ``, `a`],
  [`bus`, `ônibus`, `getting_around`, `A2`, ``, `o`],
  [`train`, `trem`, `getting_around`, `A2`, ``, `o`],
  [`ticket`, `passagem / bilhete`, `getting_around`, `A2`, `passagem = travel ticket; bilhete = event/entry ticket`, `a/o`],
  [`platform`, `plataforma`, `getting_around`, `A2`, ``, `a`],
  [`How do I get to...?`, `Como eu chego a...?`, `getting_around`, `A2`, ``, ``],
  [`Which line? (metro)`, `Qual linha?`, `getting_around`, `A2`, ``, ``],
  // ── Transactions ──────────────────────────────────────────────────
  [`water`, `água`, `transactions`, `A1`, ``, `a`],
  [`coffee`, `café`, `transactions`, `A1`, ``, `o`],
  [`bread`, `pão`, `transactions`, `A1`, ``, `o`],
  [`rice`, `arroz`, `transactions`, `A1`, ``, `o`],
  [`beans`, `feijão`, `transactions`, `A1`, `rice + beans (arroz e feijão) is the daily staple pairing`, `o`],
  [`meat`, `carne`, `transactions`, `A1`, ``, `a`],
  [`chicken`, `frango`, `transactions`, `A1`, ``, `o`],
  [`fruit`, `fruta`, `transactions`, `A1`, ``, `a`],
  [`vegetables`, `legumes / verduras`, `transactions`, `A1`, `legumes = root/firm veg; verduras = leafy greens`, `os/as`],
  [`milk`, `leite`, `transactions`, `A1`, ``, `o`],
  [`beer`, `cerveja`, `transactions`, `A1`, ``, `a`],
  [`juice`, `suco`, `transactions`, `A1`, ``, `o`],
  [`breakfast`, `café da manhã`, `transactions`, `A1`, `literally "morning coffee"`, ``],
  [`lunch`, `almoço`, `transactions`, `A1`, ``, `o`],
  [`dinner`, `jantar`, `transactions`, `A1`, ``, `o`],
  [`the bill (check)`, `a conta`, `transactions`, `A1`, ``, ``],
  [`menu`, `cardápio`, `transactions`, `A2`, ``, `o`],
  [`waiter / waitress`, `garçom / garçonete`, `transactions`, `A2`, ``, `o/a`],
  [`to order (food)`, `pedir`, `transactions`, `A2`, `same verb as "to ask for"`, ``],
  [`I'm allergic to...`, `Sou alérgico(a) a...`, `transactions`, `A2`, ``, ``],
  [`size (clothing)`, `tamanho`, `transactions`, `A2`, ``, `o`],
  [`small / medium / large`, `P / M / G`, `transactions`, `A2`, `abbreviations used on actual clothing tags`, ``],
  [`to try on`, `experimentar`, `transactions`, `A2`, ``, ``],
  [`cash`, `dinheiro`, `transactions`, `A2`, ``, `o`],
  [`card`, `cartão`, `transactions`, `A2`, ``, `o`],
  [`discount`, `desconto`, `transactions`, `A2`, ``, `o`],
  [`receipt`, `recibo`, `transactions`, `A2`, ``, `o`],
  // ── Daily Life ───────────────────────────────────────────────────
  [`to wake up`, `acordar`, `daily_life`, `A2`, ``, ``],
  [`to get ready`, `se arrumar`, `daily_life`, `A2`, ``, ``],
  [`to brush teeth`, `escovar os dentes`, `daily_life`, `A2`, ``, ``],
  [`to go to bed`, `ir para a cama / ir dormir`, `daily_life`, `A2`, ``, ``],
  [`commute`, `trajeto / deslocamento`, `daily_life`, `A2`, `less common in casual speech than verb phrases above`, `o`],
  [`to play (sport)`, `jogar`, `daily_life`, `A2`, ``, ``],
  [`to play (instrument)`, `tocar`, `daily_life`, `A2`, `different verb than sports — common mix-up`, ``],
  [`sport`, `esporte`, `daily_life`, `A2`, ``, `o`],
  [`music`, `música`, `daily_life`, `A2`, ``, `a`],
  [`to read`, `ler`, `daily_life`, `A2`, ``, ``],
  [`I like to...`, `Eu gosto de...`, `daily_life`, `A2`, `reuses "gostar" pattern`, ``],
  [`It's raining`, `Está chovendo`, `daily_life`, `A2`, ``, ``],
  [`It's sunny`, `Está com sol`, `daily_life`, `A2`, ``, ``],
  [`It's hot / cold (weather)`, `Está quente / frio`, `daily_life`, `A2`, `same words as food temp adjectives, different context`, ``],
  [`What's the weather like?`, `Como está o tempo?`, `daily_life`, `A2`, ``, ``],
  [`head`, `cabeça`, `daily_life`, `A2`, ``, `a`],
  [`stomach`, `estômago`, `daily_life`, `A2`, ``, `o`],
  [`back`, `costas`, `daily_life`, `A2`, ``, `as`],
  [`I feel sick`, `Estou passando mal`, `daily_life`, `A2`, ``, ``],
  [`It hurts`, `Dói`, `daily_life`, `A2`, `"minha cabeça dói" = my head hurts`, ``],
  [`I have a headache`, `Estou com dor de cabeça`, `daily_life`, `A2`, `"estar com dor de" + body part is the pattern`, ``],
  [`fever`, `febre`, `daily_life`, `A2`, ``, `a`],
  // ── B1: ability, habit & planning verbs ─────────────────────────────
  [`to manage to / to be able to`, `conseguir`, `daily_life`, `B1`, `different from poder — conseguir implies actually pulling it off`, ``],
  [`to try`, `tentar`, `daily_life`, `B1`, ``, ``],
  [`to decide`, `decidir`, `daily_life`, `B1`, ``, ``],
  [`to avoid`, `evitar`, `daily_life`, `B1`, ``, ``],
  [`used to (do something)`, `costumar`, `daily_life`, `B1`, `"eu costumava..." for past habits`, ``],
  [`to keep (doing)`, `continuar a`, `daily_life`, `B1`, `+ infinitive`, ``],
  [`to get used to`, `se acostumar com`, `daily_life`, `B1`, ``, ``],
  [`to make plans`, `fazer planos`, `daily_life`, `B1`, ``, ``],
  [`to take care of`, `cuidar de`, `daily_life`, `B1`, ``, ``],
  [`to be in a hurry`, `estar com pressa`, `daily_life`, `B1`, ``, ``],
  // ── Communication & Opinions ────────────────────────────────────
  [`I don't understand`, `Não entendo`, `communication_opinions`, `A1`, ``, ``],
  [`I don't speak Portuguese well`, `Não falo português bem`, `communication_opinions`, `A1`, ``, ``],
  [`Can you help me?`, `Você pode me ajudar?`, `communication_opinions`, `A1`, ``, ``],
  [`How much does it cost?`, `Quanto custa?`, `communication_opinions`, `A1`, ``, ``],
  [`Where is the bathroom?`, `Onde fica o banheiro?`, `communication_opinions`, `A1`, ``, ``],
  [`I would like...`, `Eu gostaria de...`, `communication_opinions`, `A1`, `more polite than "eu quero" (I want)`, ``],
  [`Do you have...?`, `Você tem...?`, `communication_opinions`, `A1`, ``, ``],
  [`I'm looking for...`, `Estou procurando...`, `communication_opinions`, `A1`, ``, ``],
  [`Can you repeat that?`, `Pode repetir?`, `communication_opinions`, `A1`, ``, ``],
  [`Slower, please`, `Mais devagar, por favor`, `communication_opinions`, `A1`, ``, ``],
  [`What does this mean?`, `O que isso significa?`, `communication_opinions`, `A1`, ``, ``],
  [`I'm from...`, `Eu sou de...`, `communication_opinions`, `A1`, ``, ``],
  [`My name is...`, `Meu nome é...`, `communication_opinions`, `A1`, ``, ``],
  [`I am ... years old`, `Tenho ... anos`, `communication_opinions`, `A1`, `age uses the verb ter (to have), not ser/estar like in English`, ``],
  [`I think that...`, `Eu acho que...`, `communication_opinions`, `A2`, `most common way to give an opinion, not "eu penso"`, ``],
  [`In my opinion`, `Na minha opinião`, `communication_opinions`, `A2`, ``, ``],
  [`I agree / disagree`, `Concordo / Discordo`, `communication_opinions`, `A2`, ``, ``],
  [`You think? / Really?`, `Será?`, `communication_opinions`, `A2`, `a doubtful or curious reaction; literally "will it be?" — the future of ser`, ``],
  [`We'll see`, `Veremos`, `communication_opinions`, `A2`, `veremos is the future of ver; in casual speech "vamos ver" is just as common`, ``],
  // ── B1: connectors & hedging ──────────────────────────────────────────
  [`although / even though`, `embora / apesar de`, `communication_opinions`, `B1`, `embora takes the subjunctive; apesar de + infinitive/noun doesn't`, ``],
  [`while / whereas`, `enquanto`, `communication_opinions`, `B1`, ``, ``],
  [`since / given that`, `já que`, `communication_opinions`, `B1`, ``, ``],
  [`so / therefore`, `então / portanto`, `communication_opinions`, `B1`, `então = everyday spoken; portanto = more formal/written`, ``],
  [`even if`, `mesmo que`, `communication_opinions`, `B1`, `triggers subjunctive`, ``],
  [`as soon as`, `assim que`, `communication_opinions`, `B1`, ``, ``],
  [`instead of`, `em vez de`, `communication_opinions`, `B1`, ``, ``],
  [`on the other hand`, `por outro lado`, `communication_opinions`, `B1`, ``, ``],
  [`that is (to say)`, `ou seja`, `communication_opinions`, `B1`, `extremely common filler/clarifier in speech`, ``],
  [`maybe / perhaps`, `talvez`, `communication_opinions`, `B1`, `triggers subjunctive`, ``],
  [`probably`, `provavelmente`, `communication_opinions`, `B1`, ``, ``],
  [`actually / in fact`, `na verdade`, `communication_opinions`, `B1`, ``, ``],
  [`unfortunately / fortunately`, `infelizmente / felizmente`, `communication_opinions`, `B1`, ``, ``],
  [`generally / usually`, `geralmente`, `communication_opinions`, `B1`, ``, ``],
  [`I'm not sure`, `Não tenho certeza`, `communication_opinions`, `B1`, ``, ``],
  [`It depends`, `Depende`, `communication_opinions`, `B1`, ``, ``],
  [`As far as I know`, `Pelo que eu sei`, `communication_opinions`, `B1`, ``, ``],
  [`I would rather`, `Eu prefiro`, `communication_opinions`, `B1`, ``, ``],
]

function slug(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

const usedIds = new Set<string>()

export const VOCABULARY: VocabRow[] = RAW.map(([english, portuguese, category, level, notes, article]) => {
  let id = `${category}-${slug(english)}`
  while (usedIds.has(id)) id = `${id}-x`
  usedIds.add(id)
  return {
    id,
    english,
    portuguese,
    category,
    level,
    notes: notes.trim().length > 0 ? notes : null,
    article: article ? (article.includes('/') ? article.split('/').map((part) => part.trim()) : article) : undefined,
  }
})

// Every CEFR level present in the data, ascending. The codes (A1, A2, B1 …)
// already sort in level order. The UI's level filter is built from this list —
// adding a B1 row makes a B1 chip appear with no code change.
export const VOCAB_LEVELS: string[] = [...new Set(VOCABULARY.map((row) => row.level))].sort()

// ---------------------------------------------------------------------------
// False friends — Portuguese words an English speaker misreads because they
// look or sound like an English word with a different (often opposite) sense.
// Its own view on the Vocabulary tab. One flat list, most common / highest-
// stakes first. Every single-word `pt` form is a Translate-tab headword
// (dictionaryCoverage.test).
// ---------------------------------------------------------------------------

export type FalseFriend = {
  pt: string // the Portuguese word (or "verb / noun" pair) that trips you up
  ptMeans: string // what it actually means in Portuguese
  soundsLike: string // the English word it resembles
  enInPt: string // the Portuguese you'd use when you mean that English word
  enMeans: string // what those Portuguese word(s) mean — i.e. the real sense of the look-alike
  trap: string // a short illustration of the mix-up in context
}

// [pt, ptMeans, soundsLike, enInPt, enMeans, trap]
const FF_RAW: Array<[string, string, string, string, string, string]> = [
  [`pretender`, `to intend / plan to`, `pretend`, `fingir`, `to fake it, put on an act`,
   `"Pretendo viajar" = "I intend to travel," not "I'm pretending to."`],
  [`assistir`, `to watch / to attend`, `assist`, `ajudar / auxiliar`, `to help someone`,
   `"Assisti ao jogo" = "I watched the game."`],
  [`puxar`, `to pull`, `push`, `empurrar`, `to push`,
   `A door marked PUXE means pull, not push.`],
  [`atual / atualmente`, `current / currently`, `actual / actually`, `na verdade / na realidade`, `in fact, really`,
   `"Atualmente moro aqui" = "I currently live here," not "actually."`],
  [`pasta`, `folder; toothpaste ("pasta de dente")`, `pasta`, `massa / macarrão`, `the noodle dish`,
   `"Me passa a pasta" = "hand me the folder."`],
  [`esquisito`, `weird, strange, off-putting`, `exquisite`, `requintado / primoroso`, `exceptionally fine or refined`,
   `"Que comida esquisita" is an insult, not a compliment.`],
  [`realizar`, `to carry out, accomplish`, `realize`, `perceber / dar-se conta`, `to become aware of, to notice`,
   `"Realizei um sonho" = "I made a dream come true," not "I realized a dream."`],
  [`constipado`, `having a head cold`, `constipated`, `prisão de ventre`, `unable to pass stool`,
   `At the doctor, "estou constipado" = "I have a cold" — same for "constipação".`],
  [`colégio`, `a school (roughly K–12)`, `college`, `faculdade / universidade`, `a university`,
   `"Meu filho está no colégio" is a school-age kid.`],
  [`parentes`, `relatives (any of them)`, `parents`, `pais`, `mother and father`,
   `"Tenho parentes no Sul" = "I have relatives in the South."`],
  [`novela`, `a TV soap opera`, `novel`, `romance`, `a book-length work of fiction`,
   `"A novela das nove" is the nightly soap.`],
  [`romance`, `a novel (book); also a love affair`, `romance`, `caso / romance`, `a love affair; a romantic story`,
   `"Li um romance" usually = "I read a novel." Only context tells you which sense.`],
  [`legenda`, `subtitle; caption; map key`, `legend`, `lenda`, `a myth or traditional story`,
   `"Põe a legenda" = "put the subtitles on."`],
  [`data`, `a calendar date`, `data`, `dados`, `information, figures`,
   `"Que data é hoje?" = "what's the date today?"`],
  [`fábrica`, `a factory`, `fabric`, `tecido`, `cloth, material`,
   `"Ela trabalha na fábrica" = "she works at the factory."`],
  [`livraria`, `a bookshop`, `library`, `biblioteca`, `a lending library`,
   `"Comprei na livraria" = "I bought it at the bookshop."`],
  [`idioma`, `a language`, `idiom`, `expressão idiomática`, `a fixed figurative phrase`,
   `"O idioma português" = "the Portuguese language."`],
  [`compromisso`, `an appointment, commitment, engagement`, `compromise`, `acordo / meio-termo`, `a settlement where each side concedes`,
   `"Tenho um compromisso às 3" = "an appointment at 3."`],
  [`discussão / discutir`, `an argument / to argue (often heated)`, `discussion / discuss`, `conversa / conversar`, `a calm exchange of ideas; to talk something over`,
   `"Tivemos uma discussão feia" = "we had a nasty argument."`],
  [`suportar`, `to bear, put up with, withstand`, `support`, `apoiar`, `to back, help, stand behind`,
   `"Não suporto ele" = "I can't stand him."`],
  [`educado`, `polite, well-mannered`, `educated`, `instruído / culto`, `schooled, learned`,
   `"Que menino educado" = "what a polite boy."`],
  [`ordinário`, `vulgar, crude; also low-quality`, `ordinary`, `comum / normal`, `normal, unremarkable`,
   `"Um sujeito ordinário" = "a contemptible guy" — a strong insult.`],
  [`oficina`, `a workshop; a repair garage`, `office`, `escritório`, `a place of desk work`,
   `"O carro está na oficina" = "the car's at the garage."`],
  [`taxa`, `a rate or fee`, `tax`, `imposto`, `money levied by the government`,
   `"Taxa de juros" = "interest rate"; "taxa de serviço" = "service fee."`],
  [`prejuízo`, `a loss or damage, esp. financial`, `prejudice`, `preconceito`, `a biased preconception`,
   `"A empresa teve prejuízo" = "the company took a loss."`],
  [`injúria`, `a slander / defamatory insult (legal)`, `injury`, `lesão / ferimento`, `physical harm, a wound`,
   `"Processo por injúria" is a defamation suit.`],
  [`anedota`, `a joke; an amusing story`, `anecdote`, `caso / história`, `a short real-life account`,
   `"Contou uma anedota" = "told a joke."`],
  [`contestar`, `to challenge, dispute, contest`, `contest (noun)`, `concurso / competição`, `a competition`,
   `The verb matches ("contestar a multa" = contest the fine); only the noun differs.`],
  [`êxito`, `success`, `exit`, `saída`, `a way out`,
   `"A peça foi um êxito" = "the play was a success."`],
  [`assumir`, `to take on / take up; to admit, own up to`, `assume`, `supor / presumir`, `to suppose without proof`,
   `"Assumiu o cargo" = "took up the post"; "assumiu o erro" = "owned the mistake."`],
  [`jornal`, `a newspaper; a TV news bulletin`, `journal`, `diário`, `a diary or personal journal`,
   `"Li no jornal" = "I read it in the newspaper."`],
  [`notícia`, `a piece of news`, `notice`, `aviso`, `a written announcement`,
   `"Tenho uma notícia" = "I have some news."`],
  [`balcão`, `a counter (shop, bar, reception)`, `balcony`, `varanda / sacada`, `a platform off an upper floor`,
   `"Pague no balcão" = "pay at the counter."`],
  [`eventualmente`, `occasionally; possibly`, `eventually`, `no fim / por fim`, `in the end, sooner or later`,
   `"Eventualmente acontece" = "it happens from time to time."`],
  [`simpático`, `nice, friendly, likeable`, `sympathetic`, `solidário / compreensivo`, `showing compassion for someone's hardship`,
   `"Ela é muito simpática" = "she's very friendly."`],
  [`sensível`, `sensitive (emotionally; a delicate topic)`, `sensible`, `sensato / razoável`, `practical, level-headed`,
   `"Ele é sensível" = "he's sensitive."`],
]

export const FALSE_FRIENDS: FalseFriend[] = FF_RAW.map(([pt, ptMeans, soundsLike, enInPt, enMeans, trap]) => ({
  pt,
  ptMeans,
  soundsLike,
  enInPt,
  enMeans,
  trap,
}))
